'use client';

import { useRef } from 'react';

/**
 * Native `<dialog showModal()>` lives in the top layer. Content portaled to
 * `document.body` renders underneath it and appears clipped or "behind" the
 * modal. Prefer an explicit container, otherwise attach to the open dialog so
 * the overlay stays in the top layer.
 */
export function resolveDialogPortalContainer(
  container?: HTMLElement | null,
): HTMLElement | undefined {
  if (container) return container;
  if (typeof document === 'undefined') return undefined;

  const openDialog = document.querySelector('dialog[open]');
  return openDialog instanceof HTMLElement ? openDialog : undefined;
}

/**
 * Keep overlay content attached to the dialog it opened in, even after that
 * dialog closes. Re-resolving to `document.body` while the overlay is still
 * mounted (exit animation, late close) pins it at 0,0 in the viewport.
 */
export function useDialogPortalContainer(container?: HTMLElement | null) {
  const resolved = resolveDialogPortalContainer(container);
  const lockedRef = useRef<HTMLElement | undefined>(undefined);
  if (resolved) lockedRef.current = resolved;
  return resolved ?? lockedRef.current;
}
